'use client';

import { RadiusPixel } from '@ghosts-inc/design-system/constants';
import { Flame } from '@ghosts-inc/design-system/icons';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography, { type TypographyProps } from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import Image from 'next/image';
import Link from 'next/link';
import { AnalyticsEventNames } from '@/analytics/Event.types';
import { PageContext, SectionContext } from '@/analytics/analytics.types';
import { analytics } from '@/analytics/segment';
import {
  Background,
  ListingHomePageRecommendation,
} from '@/app/(authenticated)/(marketplace)/_components/types';
import FavoriteHeart from '@/components/FavoriteHeart';
import useHoverZoom from '@/hooks/useHoverZoom';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import { logEvent } from '@/legacy/user-events/log-event';
import { UserEventTypes } from '@/legacy/user-events/user-events.types';
import {
  hasAmazonMarketplaceData,
  isGoodForStockXListing,
  isNewListing,
  isTrendingListing,
} from '@/lib/algolia/helpers';
import { s3ImageLoader } from '@/lib/cloudflare/s3ImageLoader';
import { EARLY_ACCESS_TAG_NAME } from '@/lib/constants';
import { getBrandDisplayFromBrandArray } from '@/lib/getBrandDisplayFromBrandArray/getBrandDisplayFromBrandArray';
import { ListingHit } from '@/types/ListingHit';
import { getAboveBelowMsrpFullText } from '@/utils/calculatePercentOffMsrp';
import { pluralizeWithFormattedNumber } from '@/utils/pluralizeWithFormattedNumber';
import { AmazonCircleIcon } from '../AmazonCircleIcon';
import { StockXCircleIcon } from '../StockXCircleIcon';
import ForYouActionButtons from './ForYouActionButtons';

export default function ListingCard({
  analyticsInfo,
  background,
  hit,
  isStockXSectionEnabled,
  isFavorite,
  isFirstPage = false,
  listingRecommendation,
  onFavoriteToggled,
}: {
  analyticsInfo?: {
    new?: boolean;
    trending?: boolean;
    context?: PageContext &
      SectionContext & { filters?: Record<any, any>; index?: string; itemIndex?: number };
  };
  background?: Background;
  hit: Pick<
    ListingHit,
    | 'objectID'
    | 'brands'
    | 'categories'
    | 'imageUrl'
    | 'marketplaceSources'
    | 'title'
    | 'percentOffMsrp'
    | 'totalUnits'
    | '_tags'
    | 'isPartial'
    | 'isPreview'
  >;
  isStockXSectionEnabled: boolean;
  isFavorite?: boolean;
  isFirstPage?: boolean;
  listingRecommendation?: ListingHomePageRecommendation;
  onFavoriteToggled?: () => void;
}): JSX.Element {
  const theme = useTheme();
  const brandString = getBrandDisplayFromBrandArray(hit.brands);
  const listingId = Number(hit.objectID);

  /**
   * the Intersection Observer will only observe listings in the first page as user scrolls the home page
   *  while the carousel clicks will track listing views
   */

  const handleListingView = (isElementVisibleAndFirstAppearance: boolean) => {
    if (isElementVisibleAndFirstAppearance) {
      analytics.track(AnalyticsEventNames.HOME_SECTION_LISTING_VIEW, {
        listingId: listingId,
        section: analyticsInfo?.context?.section,
      });
    }
  };

  const { elementRef } = useIntersectionObserver(handleListingView);

  const onListingClick = () => {
    analytics.track(AnalyticsEventNames.LISTING_CARD_CLICKED, {
      listing_id: listingId,
      ...analyticsInfo,
      brands: hit.brands,
      categories: hit.categories,
      tags: hit._tags,
      ...(listingRecommendation ? { match_reason: listingRecommendation.matchReason } : {}),
    });

    if (isNewListing(hit)) {
      logEvent(UserEventTypes.LISTINGS_NEW_CLICK_THROUGH, listingId);
    }

    if (isTrendingListing(hit)) {
      logEvent(UserEventTypes.LISTINGS_TRENDING_CLICK_THROUGH, listingId);
    }

    if (!isNewListing(hit) && !isTrendingListing(hit)) {
      logEvent(UserEventTypes.LISTINGS_NO_TAG_CLICK_THROUGH, listingId);
    }
  };

  const { onMouseEnter, onMouseLeave, hoverStyle } = useHoverZoom();

  const textColor: TypographyProps['color'] =
    background === 'dark' ? 'typographyDarkInverted' : 'typographyDarkPrimary';

  const isForYouListing = !!listingRecommendation;

  return (
    <Box position="relative" width="100%" ref={isFirstPage ? elementRef : null}>
      <Link href={`/listings/${listingId}`} style={{ width: '100%' }} color="inherit">
        <Stack
          style={{ width: '100%', cursor: 'pointer' }}
          onClick={onListingClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <Box style={{ aspectRatio: '1/1' }} position="relative" mb={2}>
            <Image
              loader={s3ImageLoader}
              src={hit.imageUrl}
              alt={hit.title}
              width={340}
              height={340}
              style={{
                aspectRatio: '1/1',
                objectFit: 'cover',
                borderRadius: RadiusPixel.large,
                ...hoverStyle,
              }}
            />
            {(hit.isPreview &&
              (hit._tags.includes(EARLY_ACCESS_TAG_NAME) ? (
                <Box position="absolute" top={14} left={14}>
                  <Chip
                    sx={{ px: 0 }}
                    label="Early Access"
                    variant="filled"
                    size="small"
                    color="chipDark"
                  />
                </Box>
              ) : (
                <Box position="absolute" top={14} left={14}>
                  <Chip sx={{ px: 0 }} label="Coming Soon" variant="filled" size="small" />
                </Box>
              ))) ||
              (isTrendingListing(hit) && (
                <Box position="absolute" top={8} left={8} data-testid="trending-icon">
                  <Flame sx={{ fontSize: 24 }} htmlColor={theme.customColors.trendingFlame} />
                </Box>
              )) ||
              (isStockXSectionEnabled && isGoodForStockXListing(hit) && (
                <StockXCircleIcon boxProps={{ sx: { position: 'absolute', top: 12, left: 12 } }} />
              )) ||
              (hasAmazonMarketplaceData(hit) && (
                <AmazonCircleIcon sx={{ position: 'absolute', top: 12, left: 12 }} />
              ))}
            {isForYouListing && (
              <ForYouActionButtons
                analyticsInfo={analyticsInfo}
                listingId={listingId}
                isFavorite={!!isFavorite}
              />
            )}
          </Box>
          <Typography
            variant="bodyLargeHeavy"
            color={textColor}
            data-testid="listing-card-brand-string"
          >
            {brandString}
          </Typography>
          <Typography variant="bodyLargeLight" color={textColor} data-testid="listing-card-title">
            {hit.title}
          </Typography>
          <Typography
            variant="bodySmallLight"
            color="typographyDarkSecondary"
            data-testid="listing-card-metadata"
          >
            {pluralizeWithFormattedNumber('unit', hit.totalUnits)}
            {!!hit.percentOffMsrp && ` • ${getAboveBelowMsrpFullText(hit.percentOffMsrp)}`}
          </Typography>
          {!!listingRecommendation && (
            <Typography variant="labelSmall" color="typographySuccess" mt={0.5}>
              {listingRecommendation.matchReason}
            </Typography>
          )}
        </Stack>
      </Link>
      {!isForYouListing && (
        <Box position={'absolute'} top={0} right={0}>
          <FavoriteHeart
            listingId={listingId}
            isFavorite={isFavorite}
            analyticsInfo={{ context: analyticsInfo?.context }}
            onFavoriteClick={onFavoriteToggled}
          />
        </Box>
      )}
    </Box>
  );
}
